import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Bell, Brain, ChevronRight, CreditCard, Download, FileUp, Shield, Trash2, User } from 'lucide-react';
import { buildExport, ImportError, parseImport, toCSV, type ParsedImport } from '@habitos/protocol';
import { useI18n, LOCALES, type Locale } from '../i18n';
import { useApp, type Theme } from '../state';
import { changeWeekStart, deleteVacation, deviceTimeZone, saveVacation, updateSettings, type VacationDraft } from '../domain/actions';
import { currentWeekStartOf, type VacationRec } from '../domain/model';
import { allRecords, applyRemote, importOwnerMismatch, isSyncEnabled, previewImport, wipeLocalData, type ImportPreview } from '../db/repo';
import { getMeta, setMeta } from '../db/db';
import { downloadText } from '../lib/download';
import { Field, Segmented, Switch } from '../ui/Field';
import { Sheet } from '../ui/Sheet';
import { useToast } from '../ui/Toast';
import { useSyncState } from '../sync/store';
import { hhmmToMinutes, minutesToHHMM, unitLabel } from '../ui/format';

const APP_VERSION = import.meta.env.VITE_APP_VERSION ?? '0.1.0';

export function SettingsScreen() {
  const { t, m } = useI18n();
  const { ctx, locale, setLocale, theme, setTheme } = useApp();
  const { signedIn } = useSyncState();
  const s = ctx.settings;

  return (
    <div className="stack-l">
      <h1>{t('settings.title')}</h1>

      <section className="form-section" aria-label={t('settings.general')}>
        <h2>{t('settings.general')}</h2>
        <Field label={t('settings.language')}>
          {(p) => (
            <select {...p} className="select" value={locale} onChange={(e) => setLocale(e.target.value as Locale)}>
              {LOCALES.map((l) => (
                <option key={l} value={l}>
                  {l === 'es' ? 'Español' : 'English'}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Segmented<Theme>
          legend={t('settings.theme')}
          value={theme}
          onChange={setTheme}
          options={[
            { value: 'system', label: t('settings.themes.system') },
            { value: 'light', label: t('settings.themes.light') },
            { value: 'dark', label: t('settings.themes.dark') },
          ]}
        />
        <Switch label={t('settings.hideStreaks')} checked={s.hideStreaks} onChange={(v) => void updateSettings({ hideStreaks: v })} />
      </section>

      <section className="form-section" aria-label={t('settings.time')}>
        <h2>{t('settings.time')}</h2>
        <Field label={t('settings.dayStart')} help={t('settings.dayStartHelp')}>
          {(p) => (
            <input
              {...p}
              type="time"
              className="input num"
              value={minutesToHHMM(s.dayStartMinutes)}
              onChange={(e) => e.target.value && void updateSettings({ dayStartMinutes: hhmmToMinutes(e.target.value) })}
            />
          )}
        </Field>
        <Field label={t('settings.weekStart')} help={t('settings.weekStartHelp')}>
          {(p) => (
            <select {...p} className="select" value={currentWeekStartOf(s)} onChange={(e) => void changeWeekStart(s, Number(e.target.value), ctx.today)}>
              {m.weekdays.long.map((name, i) => (
                <option key={i} value={i + 1}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Segmented
          legend={`${t('settings.timezone')}: ${s.effectiveTz}`}
          value={s.tzMode}
          onChange={(v) => {
            const device = deviceTimeZone();
            void updateSettings(v === 'follow_device' ? { tzMode: v, effectiveTz: device, homeTz: device } : { tzMode: v });
          }}
          options={[
            { value: 'follow_device', label: t('settings.tzFollow') },
            { value: 'fixed', label: t('settings.tzFixed') },
          ]}
        />
      </section>

      <Vacations />

      <nav className="list-card" aria-label={t('settings.account')}>
        <Link to="/settings/assistant" className="list-item">
          <Brain size={20} aria-hidden="true" />
          <span className="list-item__body">
            <span className="list-item__title">{t('assistantSettings.title')}</span>
            <span className="list-item__sub">{t('assistantSettings.summary')}</span>
          </span>
          <ChevronRight size={18} aria-hidden="true" />
        </Link>
        <Link to="/settings/account" className="list-item">
          <User size={20} aria-hidden="true" />
          <span className="list-item__body">
            <span className="list-item__title">{t('settings.account')}</span>
            <span className="list-item__sub">{signedIn ? t('account.settingsSignedIn') : t('account.settingsSignedOut')}</span>
          </span>
          <ChevronRight size={18} aria-hidden="true" />
        </Link>
        <Link to="/settings/notifications" className="list-item">
          <Bell size={20} aria-hidden="true" />
          <span className="list-item__body list-item__title">{t('settings.notifications')}</span>
          <ChevronRight size={18} aria-hidden="true" />
        </Link>
        <Link to="/settings/plan" className="list-item">
          <CreditCard size={20} aria-hidden="true" />
          <span className="list-item__body">
            <span className="list-item__title">{t('settings.plan')}</span>
            <span className="list-item__sub">{t('plan.settingsSub')}</span>
          </span>
          <ChevronRight size={18} aria-hidden="true" />
        </Link>
      </nav>

      <DataSection />

      <section className="form-section" aria-label={t('settings.about')}>
        <h2>{t('settings.about')}</h2>
        <div className="row">
          <Shield size={20} aria-hidden="true" />
          <p className="small">{t('settings.privacyText')}</p>
        </div>
        <p className="muted xsmall">{t('settings.version', { version: APP_VERSION })}</p>
      </section>
    </div>
  );
}

function Vacations() {
  const { t, fmtDate } = useI18n();
  const { data, ctx } = useApp();
  const toast = useToast();
  const [editing, setEditing] = useState<VacationRec | 'new' | null>(null);
  const [draft, setDraft] = useState<VacationDraft>({ name: null, startDate: ctx.today, endDate: ctx.today, scope: 'all', habitIds: [] });
  const habits = data.habits.filter((h) => !h.deletedAt && !h.archivedAt && h.kind !== 'task');

  const open = (v: VacationRec | 'new') => {
    setEditing(v);
    setDraft(v === 'new' ? { name: null, startDate: ctx.today, endDate: ctx.today, scope: 'all', habitIds: [] } : { name: v.name, startDate: v.startDate, endDate: v.endDate, scope: v.scope, habitIds: v.habitIds });
  };
  const vacations = [...data.vacations].sort((a, b) => (a.startDate < b.startDate ? 1 : -1));
  const valid = draft.endDate >= draft.startDate && (draft.scope === 'all' || draft.habitIds.length > 0);

  return (
    <section className="form-section" aria-label={t('settings.vacations')}>
      <div className="row">
        <h2 className="spacer">{t('settings.vacations')}</h2>
        <button type="button" className="btn btn--sm" onClick={() => open('new')}>
          {t('settings.newVacation')}
        </button>
      </div>
      <p className="muted small">{t('settings.vacationsHelp')}</p>
      {vacations.length === 0 ? (
        <p className="muted small">{t('settings.noVacations')}</p>
      ) : (
        <div className="list-card">
          {vacations.map((v) => (
            <button key={v.id} type="button" className="list-item" onClick={() => open(v)}>
              <span className="list-item__body">
                <span className="list-item__title">{v.name ?? t('settings.vacations')}</span>
                <span className="list-item__sub" style={{ display: 'block' }}>
                  {fmtDate(v.startDate)} – {fmtDate(v.endDate)} · {v.scope === 'all' ? t('settings.scopeAll') : `${v.habitIds.length}`}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
      <Sheet open={editing !== null} onClose={() => setEditing(null)} title={editing === 'new' ? t('settings.newVacation') : t('settings.vacations')}>
        <form
          className="stack"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!valid) return;
            try {
              await saveVacation(editing === 'new' || editing === null ? null : editing.id, draft);
              setEditing(null);
            } catch {
              toast.show(t('errors.save'));
            }
          }}
        >
          <Field label={`${t('settings.vacationName')} (${t('common.optional')})`}>
            {(p) => <input {...p} className="input" maxLength={60} value={draft.name ?? ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />}
          </Field>
          <div className="row row--wrap">
            <div style={{ flex: '1 1 140px' }}>
              <Field label={t('editor.startDate')}>
                {(p) => <input {...p} type="date" className="input" value={draft.startDate} onChange={(e) => e.target.value && setDraft({ ...draft, startDate: e.target.value })} />}
              </Field>
            </div>
            <div style={{ flex: '1 1 140px' }}>
              <Field label={t('editor.endDate')} error={draft.endDate < draft.startDate ? t('editor.errors.end_before_start') : null}>
                {(p) => <input {...p} type="date" className="input" min={draft.startDate} value={draft.endDate} onChange={(e) => e.target.value && setDraft({ ...draft, endDate: e.target.value })} />}
              </Field>
            </div>
          </div>
          <Segmented
            legend={t('settings.vacationScope')}
            value={draft.scope}
            onChange={(scope) => setDraft({ ...draft, scope })}
            options={[
              { value: 'all', label: t('settings.scopeAll') },
              { value: 'selected', label: t('settings.scopeSelected') },
            ]}
          />
          {draft.scope === 'selected' && (
            <div className="row row--wrap" role="group" aria-label={t('settings.scopeSelected')}>
              {habits.map((h) => {
                const on = draft.habitIds.includes(h.id);
                return (
                  <button
                    key={h.id}
                    type="button"
                    className="chip-toggle"
                    style={{ padding: '0 14px' }}
                    aria-pressed={on}
                    onClick={() => setDraft({ ...draft, habitIds: on ? draft.habitIds.filter((x) => x !== h.id) : [...draft.habitIds, h.id] })}
                  >
                    {h.name}
                  </button>
                );
              })}
            </div>
          )}
          <button type="submit" className="btn btn--primary btn--block" disabled={!valid}>
            {t('common.save')}
          </button>
          {editing && editing !== 'new' && (
            <button
              type="button"
              className="btn btn--danger btn--block"
              onClick={async () => {
                await deleteVacation(editing.id);
                setEditing(null);
              }}
            >
              {t('common.delete')}
            </button>
          )}
        </form>
      </Sheet>
    </section>
  );
}

function DataSection() {
  const { t, m } = useI18n();
  const { data } = useApp();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<ParsedImport | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [impact, setImpact] = useState<{ diff: ImportPreview; otherAccount: string | null } | null>(null);
  const [importing, setImporting] = useState(false);
  const [persisted, setPersisted] = useState<boolean | null>(null);
  const [confirmWipe, setConfirmWipe] = useState(false);

  useEffect(() => {
    void navigator.storage?.persisted?.().then(setPersisted);
  }, []);

  /** Cuenta propietaria de los datos del dispositivo: la actual o la última con la que se cerró sesión. */
  const owner = async () => {
    const id = await getMeta<string | null>('accountUserId', null);
    if (id) return { id, email: await getMeta<string | null>('accountEmail', null) };
    return getMeta<{ id: string; email: string | null } | null>('previousAccount', null);
  };

  const exportJson = async () => {
    const o = await owner();
    const file = buildExport(await allRecords(), APP_VERSION, new Date(), {
      source: { kind: 'device', accountId: o?.id ?? null, accountEmail: o?.email ?? null },
    });
    downloadText(`habitos-${new Date().toISOString().slice(0, 10)}.json`, JSON.stringify(file, null, 2), 'application/json');
    // Una copia recién exportada pospone el recordatorio de copia de seguridad.
    await setMeta('lastBackupNudge', Date.now());
  };

  const exportCsv = () => {
    const byId = new Map(data.habits.map((h) => [h.id, h]));
    const rows = [...data.entries]
      .sort((a, b) => (a.logicalDate < b.logicalDate ? -1 : 1))
      .map((e) => {
        const h = byId.get(e.habitId);
        const v = h?.versions[h.versions.length - 1];
        return {
          habit: h?.name ?? e.habitId,
          logical_date: e.logicalDate,
          value: e.value,
          unit: unitLabel(m, v?.unit ?? null),
          note: e.note,
          occurred_at: e.occurredAt,
          time_zone: e.tz,
          source: e.source,
        };
      });
    downloadText(
      `habitos-registros-${new Date().toISOString().slice(0, 10)}.csv`,
      '﻿' + toCSV(rows, ['habit', 'logical_date', 'value', 'unit', 'note', 'occurred_at', 'time_zone', 'source']),
      'text/csv;charset=utf-8',
    );
  };

  const onFile = async (f: File) => {
    setAnalyzing(true);
    try {
      const parsed = parseImport(await f.text());
      const [diff, o] = await Promise.all([previewImport(parsed.records), owner()]);
      setImpact({ diff, otherAccount: importOwnerMismatch(parsed.source, o?.id ?? null) ? (parsed.source?.accountEmail ?? '') : null });
      setPreview(parsed);
    } catch (err) {
      const code = err instanceof ImportError ? err.code : 'invalid_structure';
      toast.show(t(`settings.importErrors.${code}`));
    } finally {
      setAnalyzing(false);
    }
  };

  return (
    <section className="form-section" id="data" aria-label={t('settings.data')}>
      <h2>{t('settings.data')}</h2>
      <button type="button" className="btn btn--outline" onClick={() => void exportJson()}>
        <Download size={18} aria-hidden="true" /> {t('settings.exportJson')}
      </button>
      <button type="button" className="btn btn--outline" onClick={exportCsv}>
        <Download size={18} aria-hidden="true" /> {t('settings.exportCsv')}
      </button>
      <button type="button" className="btn btn--outline" disabled={analyzing} aria-busy={analyzing} onClick={() => fileRef.current?.click()}>
        <FileUp size={18} aria-hidden="true" /> {t('settings.import')}
      </button>
      <p className="muted small" aria-live="polite">
        {analyzing ? t('settings.importAnalyzing') : ''}
      </p>
      <input
        ref={fileRef}
        type="file"
        accept="application/json,.json"
        hidden
        data-testid="import-input"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void onFile(f);
          e.target.value = '';
        }}
      />
      <p className="muted xsmall">{t('settings.importHelp')}</p>
      {persisted !== null && <p className="muted xsmall">{persisted ? t('settings.storagePersisted') : t('settings.storageNotPersisted')}</p>}

      <button
        type="button"
        className="btn btn--danger"
        onClick={() => setConfirmWipe(true)}
      >
        <Trash2 size={18} aria-hidden="true" /> {t('settings.wipe')}
      </button>

      <Sheet open={confirmWipe} onClose={() => setConfirmWipe(false)} title={t('settings.wipe')}>
        <p>{t('settings.wipeConfirm')}</p>
        <div className="row">
          <button type="button" className="btn btn--outline" onClick={() => setConfirmWipe(false)}>
            {t('common.cancel')}
          </button>
          <button
            type="button"
            className="btn btn--danger spacer"
            onClick={async () => {
              await wipeLocalData();
              window.location.assign('/');
            }}
          >
            {t('common.confirm')}
          </button>
        </div>
      </Sheet>

      <Sheet open={preview !== null} onClose={() => setPreview(null)} title={t('settings.import')}>
        {preview && (
          <div className="stack">
            <p>{t('settings.importPreview', { habits: preview.counts.habits, entries: preview.counts.entries })}</p>
            {impact && (
              <p className="small" data-testid="import-impact">
                {t('settings.importImpact', { added: impact.diff.added, updated: impact.diff.updated, unchanged: impact.diff.unchanged })}
              </p>
            )}
            {preview.invalid.length > 0 && <p className="field__error">{t('settings.importInvalid', { count: preview.invalid.length })}</p>}
            {preview.unknown.length > 0 && (
              <p className="field__error">{t('settings.importUnknown', { count: preview.unknown.reduce((n, u) => n + u.count, 0) })}</p>
            )}
            {impact?.otherAccount !== null && impact && (
              <p className="field__error" role="alert">
                {t('settings.importOtherAccount', { email: impact.otherAccount || t('sync.otherAccountUnknown') })}
              </p>
            )}
            {impact && impact.diff.updated > 0 && (
              <div className="stack">
                <p className="small">{t('settings.importOverwriteHelp')}</p>
                <button type="button" className="btn btn--outline btn--block" onClick={() => void exportJson()}>
                  {t('settings.importBackupFirst')}
                </button>
              </div>
            )}
            <button
              type="button"
              className="btn btn--primary btn--block"
              disabled={importing}
              aria-busy={importing}
              onClick={async () => {
                setImporting(true);
                try {
                  const n = await applyRemote(preview.records, { enqueue: await isSyncEnabled() });
                  setPreview(null);
                  toast.show(t('settings.importDone', { count: n }));
                } catch {
                  // La transacción es atómica: si falla, no se aplica ningún cambio.
                  toast.show(t('settings.importFailed'));
                } finally {
                  setImporting(false);
                }
              }}
            >
              {t('settings.import')}
            </button>
          </div>
        )}
      </Sheet>
    </section>
  );
}
